import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { BigButton } from '../components/BigButton';
import { useExamMode } from '../context/ExamModeContext';
import { supabase } from '../lib/supabase';

interface AuditEvent {
  id: string;
  event_type: string;
  ts: string;
  metadata: Record<string, unknown> | null;
}

interface AuditSummary {
  session: {
    id: string;
    paper_title: string | null;
    candidate_name: string | null;
    candidate_number: string | null;
    started_at: string;
    ended_at: string | null;
  };
  events: AuditEvent[];
}

type EndReason =
  | 'completed'
  | 'abandoned_by_candidate'
  | 'abandoned_by_invigilator'
  | 'time_expired'
  | 'technical_fault';

// End-of-attempt screen. The candidate (or invigilator) picks an end reason
// which gets written into the exam_sessions row + an audit event. The session
// unlocks afterwards. The candidate sees a preview of the audit log inline so
// they (or the invigilator) can verify what was recorded BEFORE confirming end.
export function ExamModeEndScreen() {
  const navigate = useNavigate();
  const { session, active, end } = useExamMode();
  const [reason, setReason] = useState<EndReason>('completed');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [audit, setAudit] = useState<AuditSummary | null>(null);
  const [auditError, setAuditError] = useState<string | null>(null);

  // Load the audit log preview when the screen mounts.
  useEffect(() => {
    if (!session?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: { session: authSession } } = await supabase.auth.getSession();
        if (!authSession?.access_token) return;
        const r = await fetch(`/api/exam-audit-log?session_id=${encodeURIComponent(session.id)}`, {
          headers: { authorization: `Bearer ${authSession.access_token}` },
        });
        if (!r.ok) {
          setAuditError(`Could not load audit log (HTTP ${r.status}).`);
          return;
        }
        const body = (await r.json()) as AuditSummary;
        if (!cancelled) setAudit(body);
      } catch (e) {
        setAuditError((e as Error).message || 'Failed to load audit log.');
      }
    })();
    return () => { cancelled = true; };
  }, [session?.id]);

  async function downloadCsv() {
    if (!session?.id) return;
    const { data: { session: authSession } } = await supabase.auth.getSession();
    if (!authSession?.access_token) return;
    const url = `/api/exam-audit-log?session_id=${encodeURIComponent(session.id)}&format=csv`;
    const r = await fetch(url, {
      headers: { authorization: `Bearer ${authSession.access_token}` },
    });
    if (!r.ok) {
      setAuditError(`Could not download CSV (HTTP ${r.status}).`);
      return;
    }
    const blob = await r.blob();
    const dlUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = dlUrl;
    a.download = `audit-${session.candidateNumber || session.id.slice(0, 8)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(dlUrl);
  }

  async function handleEnd() {
    setError(null);
    setBusy(true);
    try {
      await end(reason);
      setDone(true);
    } catch (e) {
      setError((e as Error).message || 'Could not end the exam session.');
      setBusy(false);
    }
  }

  if (!active && !done) {
    return (
      <AppShell title="Exam Mode">
        <div className="px-5 py-8 max-w-md mx-auto text-center">
          <p className="text-ink/70">No active exam attempt.</p>
          <BigButton onClick={() => navigate('/app')} variant="secondary">
            Back to home
          </BigButton>
        </div>
      </AppShell>
    );
  }

  if (done) {
    return (
      <AppShell title="Attempt ended">
        <div className="px-5 py-10 max-w-md mx-auto text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-green-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Attempt ended</h1>
          <p className="mt-2 text-sm text-ink/65 leading-relaxed">
            The audit log has been written. The school can request a copy from{' '}
            <a className="text-accent underline" href="mailto:support@mydyslexiatools.com">
              support@mydyslexiatools.com
            </a>{' '}
            if needed.
          </p>
          <BigButton onClick={() => navigate('/app')} className="mt-6">
            Back to home
          </BigButton>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="End exam attempt">
      <div className="px-5 py-6 max-w-md mx-auto">
        <p className="uppercase tracking-[0.18em] text-xs text-red-700 font-semibold">
          End attempt
        </p>
        <h1 className="text-2xl font-semibold tracking-tight mt-2">
          End {session?.paperTitle || 'this attempt'}?
        </h1>
        <p className="text-sm text-ink/65 leading-relaxed mt-2">
          Picking a reason below will close the attempt, unlock AI helpers, and add a final entry
          to the audit log. This cannot be reopened.
        </p>

        <section className="mt-6 bg-white border border-ink/10 rounded-2xl p-2 space-y-1">
          {(
            [
              ['completed', 'Completed', 'The candidate finished the paper.'],
              ['time_expired', 'Time expired', 'The official exam time ran out.'],
              ['abandoned_by_candidate', 'Abandoned by candidate', 'The candidate chose to stop.'],
              [
                'abandoned_by_invigilator',
                'Abandoned by invigilator',
                'The invigilator stopped the attempt (illness, disruption, etc.).',
              ],
              ['technical_fault', 'Technical fault', 'A device or app issue ended the attempt.'],
            ] as [EndReason, string, string][]
          ).map(([value, label, description]) => (
            <label
              key={value}
              className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition ${
                reason === value ? 'bg-warm border border-amber-300' : 'hover:bg-cream/60'
              }`}
            >
              <input
                type="radio"
                name="endReason"
                value={value}
                checked={reason === value}
                onChange={(e) => setReason(e.target.value as EndReason)}
                className="mt-1.5 accent-accent"
              />
              <div>
                <p className="font-medium text-ink text-sm">{label}</p>
                <p className="text-xs text-ink/60">{description}</p>
              </div>
            </label>
          ))}
        </section>

        {/* Audit log preview - shows the candidate / invigilator what was recorded
            before they confirm end. Schools can also export the CSV for JCQ. */}
        <details className="mt-5 bg-white border border-ink/10 rounded-2xl p-4 group">
          <summary className="cursor-pointer list-none flex items-center justify-between text-sm font-medium text-ink">
            <span>Audit log preview ({audit ? audit.events.length : '...'} events)</span>
            <span className="text-ink/40 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
          </summary>
          {auditError && (
            <p className="mt-3 text-xs text-red-700">{auditError}</p>
          )}
          {audit && (
            <div className="mt-3 space-y-2 text-xs">
              <div className="grid grid-cols-2 gap-2 text-ink/70">
                <div>
                  <span className="block text-[10px] uppercase tracking-wider text-ink/45">Paper</span>
                  <span className="block text-ink">{audit.session.paper_title || '-'}</span>
                </div>
                <div>
                  <span className="block text-[10px] uppercase tracking-wider text-ink/45">Candidate</span>
                  <span className="block text-ink">{audit.session.candidate_name || '-'}{audit.session.candidate_number ? ` (${audit.session.candidate_number})` : ''}</span>
                </div>
                <div>
                  <span className="block text-[10px] uppercase tracking-wider text-ink/45">Started</span>
                  <span className="block text-ink">{formatTs(audit.session.started_at)}</span>
                </div>
                <div>
                  <span className="block text-[10px] uppercase tracking-wider text-ink/45">Events</span>
                  <span className="block text-ink">{audit.events.length}</span>
                </div>
              </div>
              <div className="mt-3 max-h-56 overflow-auto rounded-lg border border-ink/10 bg-cream/40 divide-y divide-ink/5">
                {audit.events.map((e) => (
                  <div key={e.id} className="px-3 py-1.5 grid grid-cols-[88px_1fr] gap-3">
                    <span className="text-[10px] text-ink/45 font-mono">{formatTime(e.ts)}</span>
                    <span className="text-[11px] text-ink/85">
                      <span className="font-medium">{e.event_type}</span>
                      {e.metadata && Object.keys(e.metadata).length > 0 && (
                        <span className="text-ink/45 ml-1">{JSON.stringify(e.metadata).slice(0, 90)}</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
              <button
                onClick={downloadCsv}
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-accent hover:underline"
              >
                Download .csv for the school
              </button>
            </div>
          )}
        </details>

        {error && (
          <div role="alert" className="mt-4 bg-red-50 border border-red-300 text-red-900 rounded-xl p-3 text-sm">
            {error}
          </div>
        )}

        <div className="mt-6 flex flex-col gap-3">
          <BigButton onClick={handleEnd} disabled={busy}>
            {busy ? 'Ending...' : 'End attempt'}
          </BigButton>
          <BigButton variant="secondary" onClick={() => navigate(-1)}>
            Back
          </BigButton>
        </div>
      </div>
    </AppShell>
  );
}

function formatTs(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return iso;
  }
}
